<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <div ref="smodalWrapper"
       :class="$attrs.class"
       :style="$attrs.style"
  >
    <!--eslint-disable-next-line vue/no-restricted-html-elements-->
    <b-modal
      v-bind="filteredAttrs"
      :id="modalId"
      ref="smodal"
      :visible="displayModal"
      :no-fade="isScreenReaderActive && isIOS && !enforceFade"
      :data-test="$attrs['data-test'] || 's-modal'"
      :aria-label="hideTitle ? headerTitle : undefined"
      :modal-class="modalClasses"
      :dialog-class="dialogClasses"
      :content-class="contentClasses"
      :footer-class="footerClasses"
      :hide-header="forceHideHeader || false"
      :hide-footer="hideFooter"
      :title="headerTitle"
      :scrollable="scrollable"
      :return-focus="undefined"
      :centered="centered"
      ignore-enforce-focus-selector=".sdui-lg"
      @shown="onModalShown"
      v-on="filteredListeners"
      @change="emitChange"
    >
      <template #modal-header="{ close }">
        <SModalHeader
          :class="$hasSlotContent($slots['header-progress']) ? 's-modal-header-min-height' : undefined"
          :show-default-left-action="headerShowDefaultLeftAction"
          :show-default-right-action="headerShowDefaultRightAction"
          :hide-left-action-slot="forceHideHeaderLeftActionSlot"
          :wrapper-classes="forceHideHeaderLeftActionSlot ? 'align-items-start' : undefined"
          @close-modal="close"
          @back-nav-action="$emit('header-left-action')"
        >
          <template #left-action v-if="$hasSlotContent($slots['header-left-action'])">
            <slot name="header-left-action" />
          </template>

          <template #center-action v-if="!hideTitle">
            <!-- TITLE -->
            <slot
              v-if="$hasSlotContent($slots['modal-header-title'])"
              name="modal-header-title"
              :close-fn="close"
              :modal-title-id="modalTitleId"
            />
            <div
              v-else-if="!!headerTitlePortalTarget"
              :id="modalTitleId"
              :tabindex="isScreenReaderActive ? -1 : undefined"
              data-test="modal-title-portal-target"
            >
              <portal-target :name="headerTitlePortalId" />
            </div>
            <!-- using "modalTitleId" as id enables the modal to have aria-labelledby attribute -->
            <h1 v-else
                :id="modalTitleId"
                :tabindex="isScreenReaderActive ? -1 : undefined"
                data-test="modal-title"
                class="s-modal-title"
            >
              {{ headerTitle }}
            </h1>

            <!-- SUB TITLE -->
            <slot v-if="$hasSlotContent($slots['modal-header-subtitle'])" name="modal-header-subtitle" />
            <div v-else-if="!!headerSubTitlePortalTarget" data-test="modal-subtitle-portal-target">
              <portal-target :name="headerSubTitlePortalId" />
            </div>
            <span v-else-if="headerSubTitle"
                  data-test="modal-subtitle"
                  class="s-modal-subtitle nano"
            >{{ headerSubTitle }}</span>
          </template>

          <template #right-action v-if="$hasSlotContent($slots['header-right-action'])">
            <slot name="header-right-action" />
          </template>
        </SModalHeader>

        <div v-if="$hasSlotContent($slots['header-progress'])" class="s-modal-header-progress">
          <slot name="header-progress" />
        </div>

        <div v-if="$hasSlotContent($slots['header-additional'])" class="s-modal-header-additional">
          <slot name="header-additional" />
        </div>
      </template>

      <template v-for="(index, name) in $slots" #[name]>
        <!-- The SduiNotifications are needed here to ensure that all toasts are read by the screen reader.
        When a modal opens, it is getting rendered on same level as <body>. Screenreaders are not able
        to detect aria-live changes when the element is NOT on the same DOM level as the modal. -->

        <!--eslint-disable-next-line vue/valid-v-for-->
        <sdui-notifications v-if="name === 'default' && isScreenReaderActive" />

        <!--eslint-disable-next-line vue/require-v-for-key-->
        <div v-if="name === 'modal-footer'"
             :class="{ 'pb-safe-inset': isModalOverflowing && !centered }"
             data-test="modal-footer-slot"
        >
          <slot :name="name" />
        </div>
        <slot v-else :name="name" />
      </template>

      <template v-if="!$hasSlotContent($slots['modal-footer'])" #modal-footer>
        <div :class="[footerPortalClass, { 'pb-safe-inset': isModalOverflowing && !centered }]" data-test="modal-footer-portal-target">
          <portal-target :name="footerPortalId" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { PortalTarget } from 'portal-vue'
import SModalHeader from 'src/components/base/SModal/SModalHeader.vue'
import modalFocusMixin from 'src/mixins/miscellaneous/modalFocusMixin.js'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import SduiNotifications from 'src/components/miscellaneous/ui/SduiNotifications.vue'
import {getWormholeContent} from '@/components/base/SModal/portalHelper.js'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  inheritAttrs: false,

  name: 'SModal',

  components: { PortalTarget, SduiNotifications, SModalHeader },

  mixins: [modalFocusMixin, isMobileDeviceMixin],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },

    headerTitle: {
      type: String,
      default: '',
    },

    hideTitle: {
      type: Boolean,
      default: false,
    },

    // NOTE: This prop should not be used in most use cases. It is only used in the case of special modals, such
    // as the main menu plus button. Thats also why it is not documented in storybook.
    forceHideHeader: {
      type: Boolean,
      default: false,
    },

    headerSubTitle: {
      type: String,
      default: '',
    },

    headerShowDefaultLeftAction: {
      type: Boolean,
      default: false,
    },

    forceHideHeaderLeftActionSlot: {
      type: Boolean,
      default: false,
    },

    headerShowDefaultRightAction: {
      type: Boolean,
      default: true,
    },

    modalClass: {
      type: String,
      default: 'modal-fullscreen-sm-down',
    },

    dialogClass: {
      type: String,
      default: '',
    },

    contentClass: {
      type: String,
      default: '',
    },

    footerPortalClass: {
      type: String,
      default: 'w-100',
    },

    // gradient to visually indicate that the content is scrollable
    showFooterGradient: {
      type: Boolean,
      default: true,
    },

    // modals are scrollable by default
    scrollable: {
      type: Boolean,
      default: true,
    },

    // Centers the dialog in the viewport and applies modal-class "modal-dialog-centered"
    centered: {
      type: Boolean,
      default: false,
    },

    // Element which should be focused when the modal is closed
    // Useful if modal was opened by dropdown or some async action was executed before opening the modal
    returnFocus: {
      type: [String, HTMLElement],
      default: undefined,
    },

    // re-focus the first interactive element. Useful for multi-page modals
    refocusFirstElement: {
      type: [Boolean, Number, String],
      default: false,
    },

    // Disables the default return focus behaviour when the modal is closed (focus is returned to the element that
    // opened the modal).
    preventFocusReturnOnModalClose: {
      type: Boolean,
      default: false,
    },

    enforceFade: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    modalId: null,
    portalId: null,
    displayModal: false,
    isScreenReaderActive: false,
    isIOS: false,
    isAndroid: false,
  }),

  computed: {
    filteredListeners() {
      // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api,@typescript-eslint/no-unused-vars
      const { close, ...listeners } = this.$listeners
      return listeners
    },

    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    footerPortalId() {
      return `footer-${this.portalId}`
    },

    footerPortalTarget() {
      return getWormholeContent(this.footerPortalId)?.length
    },

    headerTitlePortalId() {
      return `header-title-${this.portalId}`
    },

    headerTitlePortalTarget() {
      return getWormholeContent(this.headerTitlePortalId)?.length
    },

    headerSubTitlePortalId() {
      return `header-sub-title-${this.portalId}`
    },

    headerSubTitlePortalTarget() {
      return getWormholeContent(this.headerSubTitlePortalId)?.length
    },

    hideFooter() {
      const footerHiddenByAttrs = this.$attrs['hide-footer'] === '' || this.$attrs['hide-footer'] === true

      // check if slot exists and has content:
      const hasModalFooter = this.$hasSlotContent(this.$slots['modal-footer'])

      return footerHiddenByAttrs || (!hasModalFooter && !this.footerPortalTarget)
    },

    isModalOverflowing() {
      // sm container breakpoint: Modal will be fullscreen on mobile devices if class "modal-fullscreen-sm-down" is set
      return this.windowWidth <= 460
    },

    modalClasses() {
      if (this.centered !== undefined && this.centered !== false && this.modalClass === 'modal-fullscreen-sm-down') return ''
      return this.modalClass
    },

    dialogClasses() {
      if (this.isModalMobileLandscape) {
        if (this.dialogClass) return [this.dialogClass, 's-modal-dialog-landscape', 's-modal']
        return ['s-modal-dialog-landscape', 's-modal']
      }
      return [this.dialogClass, 's-modal']
    },

    contentClasses() {
      if (this.isModalMobileLandscape) {
        if (this.contentClass) return [this.contentClass, 's-modal-content-landscape']
        return 's-modal-content-landscape'
      }
      return this.contentClass
    },

    footerClasses() {
      const footerClasses = []
      footerClasses.push(this.$attrs['footer-class'])
      if (this.showFooterGradient) footerClasses.push('top-gradient')
      return footerClasses
    },
  },

  emits: ['update:modelValue', 'change', 'close', 'header-left-action'],

  methods: {
    emitChange(changeValue) {
      this.$emit('update:modelValue', changeValue)
      this.$emit('change', changeValue)
      if (!changeValue) this.$emit('close')
    },

    async onModalShown() {
      this.removeAriaDescribedByAttribute()
      await this.setInitialFocusElement()
    },

    removeAriaDescribedByAttribute() {
      // Bootstrap's modals automatically set aria-describedby attribute to the modal content element. This causes
      // the whole content to be read out when navigating to the title.
      this.$refs?.smodal?.$refs?.modal?.removeAttribute('aria-describedby')
    },
  },

  watch: {
    modelValue: {
      async handler(displayModal, oldDisplayModal) {
        this.displayModal = displayModal

        // modal opens, remember triggering element and add pseudo element for iOS
        if (displayModal && !oldDisplayModal) {
          await this.$nextTick()
          await this.onModalDisplayed(this.returnFocus || this.$refs?.smodal?.$_returnFocus)

          // Stop Bootstrap from returning focus to the element that opened the modal. We handle that ourselves.
          if (this.$refs?.smodal?.$_returnFocus) {
            this.$refs.smodal.$_returnFocus = undefined
          }
        }

        // modal closes, return the focus to the triggering element that opened the modal
        if (!displayModal && oldDisplayModal) {
          await this.onModalHidden(this.preventFocusReturnOnModalClose)
        }
      },

      immediate: true,
    },

    refocusFirstElement: {
      async handler(newVal, oldVal) {
        if (oldVal === newVal || !this.displayModal) return
        await this.$nextTick()
        await this.setInitialFocusElement(false)
      },
    },

    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  async mounted() {
    if (import.meta.env.IS_STORYBOOK) return
    this.isIOS = this.$store.state.isIOS
    this.isAndroid = this.$store.state.isAndroid
  },

  created() {
    this.modalId = this.$uuid()
    this.portalId = this.$uuid()
  },
}
</script>

<!--
  Unscoped:
  The Modal is beeing attached to the body (DOM), so :deep() selector does not work anymore (it was working with Vue2).
  So we have to use unscoped styles for modals.
-->
<style lang="scss">
.s-modal .modal.show .modal-dialog {
  opacity: 1;
}

.s-modal .modal-header {
  border: none !important;
  text-align: center;
  display: flex;
  align-items: center;
  border-bottom: 1px solid $color-secondary !important;
}

.s-modal .modal-footer {
  display: block !important;
  border-top: 1px solid $color-secondary !important;

  &.top-gradient {
    box-shadow: 0 0 50px 30px $color-white;
    z-index: 1050;
  }
}

.s-modal .modal-footer > * {
  margin: 0 !important;
}

@media (max-width: 460px) {
  .s-modal .modal-body {
    padding: 12px;
  }

  .s-modal .modal-header {
    padding: 12px;
  }
}

.modal--mobile-bottom .s-modal.modal-dialog {
  bottom: 0;
  position: fixed;
  left: 0;
  right: 0;
  min-height: auto;
  margin-bottom: 8px;
}

.s-modal.modal-dialog-scrollable:not(.modal-dialog-centered) {
  max-height: unset;
}

.s-modal.s-modal-dialog-landscape {
  margin: 8px auto !important;
  max-width: 75%;
  max-height: calc(100vh - 1rem) !important;
}

.s-modal .s-modal-content-landscape {
  max-height: calc(100vh - 1rem) !important;
}
</style>

<style scoped lang="scss">
.s-modal-title {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */
  font-size: $h3-font-size;
  line-height: pxtorem(24) !important;
  letter-spacing: -0.2px;
  margin: 0;
  padding: 0;
}

.s-modal-header-progress {
  width: 100%;

  :deep(.progress) {
    border-radius: 0 !important;
    height: 4px;
    margin-left: -12px;
    margin-bottom: -9px;
    width: calc(100% + 24px);
    margin-top: 4px;
  }
}

.s-modal-header-min-height {
  min-height: 44px;
}

.s-modal-header-additional {
  width: 100%;
}
</style>
